// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Starts loading a site's digital twin as soon as the site resolves, rather
 * than waiting for a twin page to mount (ADR-0008, Decision 5).
 *
 * Mounted once in the app shell, so it covers every way the active site can
 * change — the header dropdown, the fleet-overview tiles, and URL sync — because
 * all three write the same `selectedSiteId`. It also fires on the first
 * authenticated paint, since the selection is restored from persisted shell
 * state (or defaults to the user's first assigned site).
 *
 * Nothing awaits the prefetch, so it never blocks the shell. By the time the
 * user opens Knowledge Graph or Assets the graph is cached, or at worst the page
 * joins a request already in flight.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { twinQueryOptions } from './useDigitalTwin';
import { useActiveSite } from './useActiveSite';
import { useAuth } from './useAuth';

export function useTwinPrefetch(): void {
  const queryClient = useQueryClient();
  const { sites } = useAuth();
  const { siteId, isFleet } = useActiveSite();

  // Waits for /me and the OMS sites call, so a persisted selection the user no
  // longer has never reaches the BFF (where it would read as a denial).
  const twinSiteId = siteId && sites.some(s => s.site_id === siteId) ? siteId : undefined;

  useEffect(() => {
    // Whole-fleet mode has no single graph to warm.
    if (isFleet || !twinSiteId) return;

    // Structural first: it's the smaller payload and the one that paints.
    void queryClient.prefetchQuery(twinQueryOptions(twinSiteId, ''));
    void queryClient.prefetchQuery(twinQueryOptions(twinSiteId, 'state'));
  }, [queryClient, twinSiteId, isFleet]);
}
